import Skeleton from '../Skeleton';
import { homeStackProps } from '../../dashboard/home/homeTokens';
import useIsLandscapePhone from '../../../hooks/ui/useIsLandscapePhone';
import {
  Bar, SkelCard, SkelFigure, SkelGroupLabel, SkelLegend, SkelRows, skeletonHomeLayout,
} from './homeSkeletonParts';

/**
 * Platform (super-admin) dashboard skeleton — KAN-972. It mirrors the rebuilt platform home
 * (PlatformDashboardBody): the same stack (`homeStackProps` -> the 12-column grid on web, one column
 * under the dock), the same card order and `data-span`s, and the real HomeCard shell + `cvx-home-*`
 * classes (homeDashboard.css / homePlatform.css / homeColumns.css) filled with skeleton bars, so a
 * spacing change in those sheets moves the skeleton with it. Keep the ORDER in step with the body:
 *
 *   web + tablet                 Needs you 6 | Health 6 · Companies 6 | Seats 6 (pairSpans, KAN-1003)
 *   phone sideways               Needs you (two lanes) · Health · Companies (figure beside chart) · Seats
 *   phone upright                Needs you (the FOLDED summary row) · Health · Companies · Seats
 *
 * `isRoot` = the viewer is the root super admin (Health has a fifth row, Open invites, only for them). It
 * defaults to false; the caller passes the viewer's flag.
 * The body is padded by PlatformDashboardTab's wrapper, so the stack carries no inset of its own.
 */

const LANE_ROWS = 4;

/** Needs you: two lanes of label + rows (stacked at half width, side by side on a phone held sideways), or — on a phone held upright — the folded summary row. */
function NeedsYouSkeleton({ layout, folded }) {
  const web = layout === 'web';
  return (
    <SkelCard layout={layout} span={layout === 'phone' ? undefined : 6} stretch={web} testId="platform-skeleton-needs-you" titleWidth={96} action={56}>
      {folded ? (
        <div className="cvx-home-nysum" aria-hidden="true">
          <span className="cvx-home-nysum__tx">
            <span className="cvx-home-nysum__pills">
              <Skeleton width={72} height={22} borderRadius={999} />
              <Skeleton width={92} height={22} borderRadius={999} />
            </span>
            <Bar width="82%" height={11} line={18} />
          </span>
          <Skeleton width={16} height={16} borderRadius={8} />
        </div>
      ) : (
        <div className="cvx-home-lanes" data-cols={layout === 'phone' ? 2 : 1} aria-hidden="true">
          {[0, 1].map((i) => (
            <div className="cvx-home-lane" key={i}>
              <SkelGroupLabel />
              <SkelRows n={LANE_ROWS} />
            </div>
          ))}
        </div>
      )}
    </SkelCard>
  );
}

/** Health: mark + label + figure rows (four for a non-root viewer, five with Open invites for the root one). */
function HealthSkeleton({ layout, isRoot }) {
  const web = layout === 'web';
  return (
    <SkelCard layout={layout} span={layout === 'phone' ? undefined : 6} stretch={web} testId="platform-skeleton-health" titleWidth={70} action={56}>
      <SkelRows n={isRoot ? 5 : 4} square />
    </SkelCard>
  );
}

/** "New per month": six slim bars of different heights over their month labels (the real HomeColumns boxes). */
function ColumnsSkeleton() {
  const heights = [0.42, 0.7, 0.84, 0.56, 1, 0.78];
  return (
    <div className="cvx-home-cols" aria-hidden="true" style={{ '--cvx-home-cols-n': heights.length }}>
      {heights.map((p, i) => (
        <span className="cvx-home-col" key={i}>
          <span className="cvx-home-col__track">
            <span className="cvx-home-col__stack">
              <Skeleton width={14} height="var(--cvx-home-col-label)" borderRadius={6} />
              <Skeleton width="var(--cvx-home-col-w)" height={`calc(${p} * var(--cvx-home-col-max))`} borderRadius="6px 6px 2px 2px" />
            </span>
          </span>
          <Bar width={26} height={10} line={18} />
        </span>
      ))}
    </div>
  );
}

/** Companies: figure + trend note, one stacked bar + dot legend, a hairline, then the "new per month" head and columns. */
function CompaniesSkeleton({ layout }) {
  const web = layout === 'web';
  return (
    <SkelCard
      layout={layout}
      span={layout === 'phone' ? undefined : 6}
      stretch={web}
      testId="platform-skeleton-companies"
      titleWidth={100}
      action={84}
    >
      <div className="cvx-home-co" aria-hidden="true">
        <div className="cvx-home-co__main">
          <SkelFigure layout={layout} inline />
          <div className="cvx-home-co__chart">
            <Skeleton width="100%" height={8} borderRadius={999} />
            <Bar width="82%" height={11} line={20} />
          </div>
        </div>
        <i className="cvx-home-co__rule" />
        <div className="cvx-home-co__new">
          <div className="cvx-home-co__newhead">
            <Bar width={96} height={11} line={24} />
            <Bar width={72} height={11} line={24} />
          </div>
          <ColumnsSkeleton />
        </div>
      </div>
    </SkelCard>
  );
}

/** Seats: hero, the bar, the legend as rows under it and the seat-limit row. */
function SeatsSkeleton({ layout }) {
  const web = layout === 'web';
  return (
    <SkelCard layout={layout} span={layout === 'phone' ? undefined : 6} stretch={web} testId="platform-skeleton-seats" titleWidth={64} action={44}>
      <div className="cvx-home-seats" aria-hidden="true">
        <SkelFigure layout={layout} inline />
        <Skeleton width="100%" height={8} borderRadius={999} />
        <SkelLegend rows={3} lines />
        <div className="cvx-home-seats__lim"><Bar width="52%" height={10} line={18} /></div>
      </div>
    </SkelCard>
  );
}

export function PlatformDashboardSkeleton({ isMobile = false, isTablet = false, isRoot = false }) {
  const layout = skeletonHomeLayout(isMobile, isTablet);
  const sideways = useIsLandscapePhone() && layout !== 'web';
  const { style, ...rest } = homeStackProps(layout);
  return (
    <div
      {...rest}
      data-home-platform=""
      style={{
        ...style, width: '100%', minWidth: 0, boxSizing: 'border-box',
      }}
      data-testid="platform-dashboard-skeleton"
      data-skeleton-layout={layout}
    >
      <NeedsYouSkeleton layout={layout} folded={layout === 'phone' && !sideways} />
      <HealthSkeleton layout={layout} isRoot={isRoot} />
      <CompaniesSkeleton layout={layout} />
      <SeatsSkeleton layout={layout} />
    </div>
  );
}
